/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './site-group-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {SiteGroupApiService} from '../../site-group/site-group-api.service';

@Component({
  standalone: false,
  selector: 'adn-site-group-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteGroupTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.siteGroup;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  readonly siteGroups = signal<any[]>([]);
  readonly resolved = signal(false);

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const model = this.model || {};
      const summary = (model.negated === true ? 'NOT: ' : '') + _.map(
        _.filter(this.siteGroups(), (site) => (model.siteGroups || []).indexOf(site.id) > -1),
        'name'
      ).join(', ');
      return {
        vm: _.cloneDeep(model),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.siteGroup,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly siteGroupApi: SiteGroupApiService
  ) {
  }

  ngOnInit(): void {
    this.ensureModel();

    if (!this.tabController) {
      return;
    }

    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.siteGroup.widget, () => this.loadSiteGroups(), copyTemplate);
  }

  selectAll(): void {
    this.model.siteGroups = _.map(this.siteGroups(), 'id');
  }

  deselectAll(): void {
    this.model.siteGroups = [];
  }

  isSelected(siteGroupId: string): boolean {
    return (this.model.siteGroups || []).indexOf(siteGroupId) > -1;
  }

  toggleSiteGroup(siteGroupId: string): void {
    this.model.siteGroups = this.model.siteGroups || [];
    const idx = this.model.siteGroups.indexOf(siteGroupId);
    if (idx > -1) {
      this.model.siteGroups.splice(idx, 1);
    } else {
      this.model.siteGroups.push(siteGroupId);
    }
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    this.model.siteGroups = this.model.siteGroups || [];
    if (_.get(this.model.siteGroups, '[0].id')) {
      this.model.siteGroups = _.map(this.model.siteGroups, (s) => s.id);
    }
    if (_.isNil(this.model.negated)) {
      this.model.negated = false;
    }
  }

  private loadSiteGroups(): void {
    if (this.resolved()) {
      return;
    }
    this.siteGroupApi.listAll().subscribe((page) => {
      this.siteGroups.set(page.results || []);
      this.resolved.set(true);
    });
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {siteGroups: []};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.ensureModel();
  }
}
